import { colorValue } from '@hai/ui-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Status } from 'src/js/component/actionBar/actionBar';
import { SortType } from 'src/js/component/sort-manager';
import { triggerEvent, watchEvent } from 'src/js/events';
import { useAdaptiveFetch } from 'src/js/hook/use-adaptive-fetch';
import { useStores } from 'src/js/hook/use-stores';
import { NotificationVariant } from 'src/js/notification/notification';
import { createAndDispatchNotification } from 'src/js/notification/notification-helper';
import { useWatchObject } from 'src/js/store/use-watch';

import { StreamingActionBar } from 'src/js/component/actionBar/streaming-action-bar';
import { ScteList } from './scte-list';
import { ScteViewModel, scteIsStarted, scteIsStopped, scteModelToStatus } from './scte-models';
import scteModels, { fetchScte, startStopScte } from './scte-store';
import { useLocalStorage } from 'usehooks-ts';
import { constant } from 'src/js/constant';

let connectionLost = false;
export const SctePage: React.FunctionComponent = (props) => {
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  const [scte, setScte] = useState<ScteViewModel[]>(scteModels);
  useWatchObject(scteModels, setScte);
   const [isLoading, setIsLoading] = useState(true);

  useAdaptiveFetch(
    () =>
      fetchScte(t).then((res) => {
                  setIsLoading(false);
        if (res.ok) {
          if (connectionLost) {
            triggerEvent('connection-restored');
            connectionLost = false;
          }
        } else if (res.problem === 'NETWORK_ERROR') {
          triggerEvent('connection-lost');
          connectionLost = true;
        }
        return res;
      }),
    5000,
  );

  watchEvent('preset-load', () => {
    fetchScte(t);
  });

  const filteredStart = () => scte.filter((e) => e.selected && scteIsStopped(e.state));
  const handleStartEncoder = () => {
    const filtered = filteredStart();
    if (filtered.length > 0) {
      Promise.all(filtered.map((e) => startStopScte(e, true, true, t))).then((responses) => {
        triggerEvent('preset-update');
        const failed = responses.filter((r) => !r.ok);
        if (failed.length > 1) {
          // Message also in start/stop
          createAndDispatchNotification(
            t('scte.notifications.startErrorMsg', { count: failed.length }),
            NotificationVariant.ERROR,
            t,
          );
        }
      });
    }
  };

  const filteredStop = () => scte.filter((e) => e.selected && scteIsStarted(e.state));
  const handleStopEncoder = () => {
    const filtered = filteredStop();
    if (filtered.length > 0) {
      Promise.all(filtered.map((e) => startStopScte(e, false, true, t))).then((responses) => {
        triggerEvent('preset-update');
        const failed = responses.filter((r) => !r.ok);
        if (failed.length > 1) {
          // Message also in start/stop
          createAndDispatchNotification(
            t('scte.notifications.stopErrorMsg', { count: failed.length }),
            NotificationVariant.ERROR,
            t,
          );
        }
      });
    }
  };

  const statusTypes: Status[] = [
    { status: 'ACTIVE', color: colorValue('haiui-green-01') },
    { status: 'ALERT', color: colorValue('haiui-amber-01') },
    { status: 'INACTIVE', color: colorValue('haiui-gray-07') },
  ];

  const handleSelect = (item: ScteViewModel, selected: boolean) => {
    const encoders = scte.map((i: ScteViewModel) => (i.id === item.id ? { ...i, selected } : i));
    setScte(encoders);
  };

  const onSelectAll = (selected: boolean) => {
    const models = scte.map((i) => ({ ...i, selected }));
    setScte(models);
  };

  const [searchFilters, setSearchFilters] = useLocalStorage(
    `${constant.lStorage.searchFilter}:${sessionStore.username}:scte`,
    [],
  );

  const sortInfo = {
    id: { title: t('sort.sortText.id'), type: SortType.GENERIC },
    name: { title: t('sort.sortText.name'), type: SortType.NATURAL_SORT },
    stateSortable: { title: t('sort.sortText.status'), type: SortType.GENERIC },
  };

  return (
    <StreamingActionBar
      items={scte}
      searchKeys={['name', 'syncAutoTxt']}
      initialSearchFilters={searchFilters}
      isLoading={isLoading}
      onChangeSearchFilters={setSearchFilters}
      sortInfo={sortInfo}
      statusTypes={statusTypes}
      getStateValue={scteModelToStatus}
      scrollToId={(props as any).scrollToId}
      actionButtons={
        sessionStore.isUser()
          ? []
          : [
              { onClick: handleStartEncoder, counter: filteredStart, label: t('general.start') },
              { onClick: handleStopEncoder, counter: filteredStop, label: t('general.stop') },
            ]
      }
      List={ScteList}
      onSelectAll={onSelectAll}
      updateSelection={handleSelect}
      storageKey="scte"
    />
  );
};
